import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, LayoutGrid, QrCode, Sparkles } from "lucide-react";
import { useEffect } from "react";
import { Orb } from "@/components/Orb";
import { PfizerLogo } from "@/components/PfizerLogo";

export default function Landing() {
  const [params] = useSearchParams();
  const sessionParam = params.get("s");

  // If arriving via a session QR code (e.g. ?s=1), auto-redirect straight into the participant flow
  useEffect(() => {
    if (sessionParam) {
      const t = setTimeout(() => {
        window.location.href = `/build?s=${sessionParam}`;
      }, 1800);
      return () => clearTimeout(t);
    }
  }, [sessionParam]);

  if (sessionParam) return <ParticipantHandoff session={sessionParam} />;
  return <PresenterHub />;
}

function ParticipantHandoff({ session }: { session: string }) {
  return (
    <div className="relative min-h-dvh flex flex-col items-center justify-center px-6 py-10 text-center overflow-hidden">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <Orb size={200} intensity="thinking" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="mt-10 max-w-md"
      >
        <div className="inline-flex items-center gap-1.5 border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/60">
          <Sparkles className="h-3 w-3" /> Pfizer AI Workshop
        </div>
        <h1 className="mt-4 text-4xl font-bold leading-[1.05] text-white sm:text-5xl">
          Build your <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400">AI teammate</span>
        </h1>
        <p className="mt-4 text-base leading-relaxed text-white/70">
          In 3 minutes, design an AI that takes the first 80% off one task in your job.
        </p>
        <Link to={`/build?s=${session}`} className="btn-primary mt-8">
          Start building <ArrowRight className="h-4 w-4" />
        </Link>
        <div className="mt-4 text-xs text-white/40">Session {session}</div>
      </motion.div>
    </div>
  );
}

function PresenterHub() {
  return (
    <div className="relative min-h-dvh px-6 py-10 overflow-hidden">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <PfizerLogo />
          <div className="text-[11px] uppercase tracking-[0.25em] text-white/40">Presenter hub</div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-16 text-center"
        >
          <h1 className="text-5xl font-bold leading-[1.05] text-white sm:text-6xl">
            You can build the<br />
            <span className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400">
              future today.
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/60">
            Live demo for the Pfizer keynote. Pick a session to open its QR code on the big screen.
          </p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[1, 2, 3].map((n, i) => (
            <motion.div
              key={n}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.08, duration: 0.5 }}
            >
              <SessionCard n={n} />
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          <Link to="/stage?s=all&tab=marketplace" className="btn-ghost justify-start">
            <LayoutGrid className="h-4 w-4" />
            Open the marketplace (all sessions)
          </Link>
          <Link to="/stage?s=all&tab=stats" className="btn-ghost justify-start">
            <Sparkles className="h-4 w-4" />
            See combined results
          </Link>
        </motion.div>

        <div className="mt-16 border-t border-white/5 pt-6 text-center text-[11px] uppercase tracking-widest text-white/30">
          Built for Pfizer · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}

function SessionCard({ n }: { n: number }) {
  return (
    <Link
      to={`/stage?s=${n}&tab=qr`}
      className="glass glow-border group block rounded-xl p-6 transition hover:bg-white/[0.06]"
    >
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-white/50">Session</div>
        <QrCode className="h-4 w-4 text-white/40 transition group-hover:text-cyan-glow" />
      </div>
      <div className="mt-3 text-6xl font-bold text-white tabular-nums">{n}</div>
      <div className="mt-4 flex items-center gap-1.5 text-sm text-cyan-glow">
        Open QR screen
        <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}
